import { ApiError } from '../api/ApiError';

/** The shape `openapi-fetch` returns for every call. */
interface FetchResult<TData> {
  readonly data?: TData | undefined;
  readonly error?: unknown;
  readonly response: Response;
}

/** RFC 9457 problem details, which the API returns for every handled failure. */
interface ProblemDetails {
  readonly title?: string;
  readonly detail?: string;
}

/**
 * Turns an `openapi-fetch` result into data, or throws a typed {@link ApiError}.
 *
 * The API answers failures with problem details carrying a message written for a person. Surfacing
 * that `detail` instead of a generic "request failed" is the difference between a user who knows
 * their VIN has a typo and one who does not.
 *
 * @param result - The result of a client call.
 * @param fallbackMessage - Used when the response carried no usable detail.
 * @returns The response body.
 * @throws {ApiError} The request did not succeed.
 */
export function unwrap<TData>(result: FetchResult<TData>, fallbackMessage: string): TData {
  const { data, error, response } = result;

  if (error !== undefined || data === undefined) {
    const problem = error as ProblemDetails | undefined;

    throw new ApiError(
      response.status,
      problem?.detail ?? problem?.title ?? `${fallbackMessage} (HTTP ${response.status})`,
    );
  }

  return data;
}

/**
 * Turns an `openapi-fetch` result for a no-content call into nothing, or throws.
 *
 * A 204 has no body, so {@link unwrap} would treat the absent data as a failure.
 *
 * @param result - The result of a client call.
 * @param fallbackMessage - Used when the response carried no usable detail.
 * @throws {ApiError} The request did not succeed.
 */
export function unwrapEmpty(result: Omit<FetchResult<unknown>, 'data'>, fallbackMessage: string): void {
  const { error, response } = result;

  if (!response.ok || error !== undefined) {
    const problem = error as ProblemDetails | undefined;

    throw new ApiError(
      response.status,
      problem?.detail ?? problem?.title ?? `${fallbackMessage} (HTTP ${response.status})`,
    );
  }
}
